/**
 * Generic Wizard Header
 * Configurable header with title, actions, and progress indicator
 */

import React from 'react';
import { X, Save, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useWizardContext } from './WizardContext';
import { WizardHeaderProps } from './types';
import WizardProgressIndicator from './WizardProgressIndicator';

const WizardHeader: React.FC<WizardHeaderProps> = ({ clientName, onClose }) => {
  const {
    config,
    isSaving,
    lastSavedAt,
    isDirty,
    enableAutoSave,
    setEnableAutoSave,
    saveProgress
  } = useWizardContext();

  const { theme, features, formTitle, headerActions } = config;

  return (
    <header className={`${theme.headerBg} ${theme.headerText} shadow-lg relative z-20`}>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Title and client info */}
          <div className="flex items-center gap-6">
            <div>
              <h1 className="text-xl font-semibold">{formTitle}</h1>
              <p className="text-sm text-gray-300 mt-1">Client: {clientName}</p>
            </div>
          </div>

          {/* Progress indicator */}
          {features.progressIndicator && (
            <WizardProgressIndicator />
          )}

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* Auto-save toggle */}
            {features.autoSave && (
              <div className="flex items-center gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableAutoSave}
                    onChange={(e) => setEnableAutoSave(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Auto-save</span>
                </label>
              </div>
            )}

            {/* Save status */}
            {lastSavedAt && (
              <div className="flex items-center gap-1 text-sm text-gray-300">
                <Clock className="w-3.5 h-3.5" />
                <span>Saved {format(lastSavedAt, 'HH:mm')}</span>
              </div>
            )}

            {/* Custom header actions */}
            {headerActions && headerActions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={action.onClick}
                  disabled={action.disabled || action.loading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                    ${action.variant === 'primary'
                      ? 'bg-white bg-opacity-20 hover:bg-opacity-30'
                      : action.variant === 'secondary'
                      ? 'bg-white bg-opacity-10 hover:bg-opacity-20'
                      : 'hover:bg-white hover:bg-opacity-10'
                    }
                  `}
                >
                  {action.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : Icon ? (
                    <Icon className="w-4 h-4" />
                  ) : null}
                  <span className="text-sm">{action.label}</span>
                </button>
              );
            })}

            {/* Manual save */}
            {features.autoSave && (
              <button
                onClick={() => saveProgress(true)}
                disabled={isSaving || !isDirty}
                className="flex items-center gap-2 px-3 py-1.5 bg-white bg-opacity-10 rounded-md
                         hover:bg-opacity-20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Save progress"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                    <span className="text-sm">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span className="text-sm">Save</span>
                  </>
                )}
              </button>
            )}

            {/* Close button */}
            {onClose && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-colors cursor-pointer"
                aria-label="Close wizard"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default WizardHeader;
